import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import {
  Badge,
  Breadcrumb,
  ErrorState,
  Metric,
  Panel,
  Progress,
  Tabs,
  formatDate,
  formatMoney,
} from 'escrowbuild-ui';

import { useFixtureVocabulary, useT } from '@/i18n';
import { findProject, projectTotals, scheduleOf } from '@/data/domain';
import { PROJECT_TABS } from '@/app/nav';

/**
 * The six tabs, from the one registry the rest of the shell reads.
 *
 * A local list here would be a second place a project screen can be named, and
 * the browser tab — which derives its title from the same registry — would be
 * free to disagree with the tab strip about what this screen is called.
 */
const TABS = [{ value: '', labelKey: 'nav.tab.overview' } as const, ...PROJECT_TABS];

/**
 * The shared frame for every project-scoped screen.
 *
 * The development's identity and its headline numbers are resolved here once, so
 * the six tabs cannot disagree about the project's name, phase or spend. That is
 * the point of the shell rather than a convenience: a tab author given the raw
 * fixtures would be free to derive a balance slightly differently, and two
 * adjacent screens showing two different balances is the fastest way to lose a
 * client's confidence in the whole demo.
 *
 * The tab set is the document's own decomposition of a development — §10's
 * timeline, §9's finances, §5's evidence, §8's participations, §11's publication
 * gate.
 */
export function ProjectShell() {
  const { projectId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const vocabulary = useFixtureVocabulary();
  const project = findProject(projectId);

  if (!project) {
    return (
      <Panel padding="lg" title={t('project.shell.notFoundTitle')}>
        <ErrorState
          variant="page"
          title={t('project.shell.notFoundHeading')}
          message={t('project.shell.notFoundMessage', { id: projectId ?? '' })}
        />
      </Panel>
    );
  }

  const totals = projectTotals(project.id);
  const phases = scheduleOf(project.id);
  const current = phases.find((phase) => phase.status === 'in-progress');
  const nextPlanned = phases.find((phase) => phase.status === 'planned');

  // The active tab is derived from the URL, so a tab is linkable and the back
  // button works — a tab whose state lives only in a component cannot be shared.
  const activeTab =
    TABS.find((tab) => tab.value !== '' && pathname.endsWith(`/${tab.value}`))?.value ?? '';

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[{ label: t('nav.platform.projects'), href: '/projects' }, { label: project.name }]}
      />

      <div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
              {project.name}
            </h1>
            <p className="mt-1 text-sm text-muted">
              {project.address} · {project.city} · {project.lot}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Badge tone="info" variant="subtle">
              {vocabulary.phase(current ? current.id : project.phase)}
            </Badge>
            <Badge tone="neutral" variant="subtle">
              {t('project.shell.sale', { date: formatDate(project.projectedSale) })}
            </Badge>
          </div>
        </div>

        <div className="mt-4 max-w-2xl">
          <Progress
            value={project.progress}
            tone="primary"
            label={t('project.shell.progress', { name: project.name })}
            showValue
          />
          {nextPlanned ? (
            <p className="mt-2 text-xs text-muted">
              {t('project.shell.next', {
                phase: vocabulary.phase(nextPlanned.id),
                date: formatDate(plannedStartIso(project.id, nextPlanned.id)),
              })}
            </p>
          ) : null}
        </div>
      </div>

      {/*
        Four tiles, and the distinction between the first two is deliberate:
        what investors have PROMISED and what they have PAID are different
        numbers — 1,300,000 against 1,240,000 on Lakeview — and a header that
        shows one while labelling it the other is the kind of small lie that
        costs a client's confidence when they check it against the contract.

        The fourth tile is variance on work STARTED, not on the whole phased
        budget. Against the whole budget a project two-thirds built always reads
        as a large underspend, which is arithmetic on work not yet ordered
        rather than a saving.
      */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label={t('project.shell.committed')}
          value={totals.committed}
          format="money"
          hint={
            totals.uncalled > 0
              ? t('project.shell.uncalled', { amount: formatMoney(totals.uncalled) })
              : t('project.shell.fullyCalled')
          }
        />
        <Metric
          label={t('project.shell.contributed')}
          value={totals.contributed}
          format="money"
          tone="info"
          hint={t('project.shell.unspent', { amount: formatMoney(totals.available) })}
        />
        <Metric label={t('project.shell.spent')} value={totals.actual} format="money" />
        <Metric
          label={t('project.shell.variance')}
          value={totals.varianceStarted}
          format="money"
          tone={totals.varianceStarted <= 0 ? 'success' : 'danger'}
          // The delta is the variance as a share of the started budget, NOT the
          // variance again — a tile that prints one figure twice reads as two
          // corroborating measurements when it is one.
          delta={totals.startedBudgeted === 0 ? 0 : (totals.varianceStarted / totals.startedBudgeted) * 100}
          deltaFormat="percentage"
          deltaLabel={t('project.shell.varianceBasis', {
            amount: formatMoney(totals.startedBudgeted),
          })}
          trend={totals.varianceStarted <= 0 ? 'down' : 'up'}
        />
      </div>

      <Tabs
        items={TABS.map((tab) => ({ value: tab.value, label: t(tab.labelKey) }))}
        value={activeTab}
        renderPanels={false}
        label={t('project.shell.sections', { name: project.name })}
        onValueChange={(value) =>
          navigate(value === '' ? `/projects/${project.id}` : `/projects/${project.id}/${value}`)
        }
        variant="underline"
      />

      <Outlet context={{ project, totals }} />
    </div>
  );
}

/**
 * The schedule stores positions on a 12-month axis rather than dates, so the
 * "next phase" line derives a date from the project's last update plus the
 * phase's offset. Keeping the conversion in one place means no tab invents its
 * own date arithmetic — or its own date format.
 */
function plannedStartIso(projectId: string, phaseId: string): string {
  const phases = scheduleOf(projectId);
  const index = phases.findIndex((phase) => phase.id === phaseId);
  const project = findProject(projectId);
  const base = new Date(`${project?.updatedAt ?? '2026-09-01'}T00:00:00`);
  base.setMonth(base.getMonth() + Math.max(index, 0) * 2 + 1);
  return base.toISOString().slice(0, 10);
}
